import { Card, CardContent } from "@/shared/ui/card";

import { engineLabel, type GeoReport } from "../../api/geoService";
import { CitationSources } from "./report/CitationSources";
import { DimensionRadar } from "./report/DimensionRadar";
import { BrandPortrait, NegativeExposure } from "./report/InsightCards";
import { PlatformMatrix } from "./report/PlatformMatrix";
import { PresenceChecklist } from "./report/PresenceChecklist";
import { ProbeWall } from "./report/ProbeWall";
import { ScoreHero } from "./report/ScoreHero";
import { SearchInsight } from "./report/SearchInsight";
import { StrategyTopics } from "./report/StrategyTopics";

/**
 * GEO 可见度报告(A4 大改):总分 → 雷达 → 引擎矩阵 → 原话回放墙 → 阵地/引用源 →
 * 负面/画像 → 内容策略。全部子卡防御式:旧报告缺字段的卡自动隐藏。
 */
export function GeoReportPanel({
  report,
  prevScore,
}: {
  report: GeoReport;
  prevScore?: number | null;
}) {
  return (
    <div className="space-y-4">
      {/* section id = E0 详情页锚点导航目标(GeoReportDetailPage.ANCHORS) */}
      <section id="geo-sec-hero">
        <ScoreHero report={report} prevScore={prevScore} />
      </section>
      <section id="geo-sec-radar">
        <DimensionRadar report={report} />
      </section>
      <section id="geo-sec-search">
        <SearchInsight report={report} />
      </section>
      <section id="geo-sec-matrix">
        <PlatformMatrix report={report} />
      </section>
      <section id="geo-sec-probes">
        <ProbeWall report={report} />
      </section>
      <section id="geo-sec-presence" className="grid gap-4 md:grid-cols-2">
        <PresenceChecklist report={report} />
        <CitationSources report={report} />
      </section>
      <div className="grid gap-4 md:grid-cols-2">
        <NegativeExposure report={report} />
        <BrandPortrait report={report} />
      </div>
      <section id="geo-sec-comp" className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-2 p-6">
            <div className="text-sm font-medium">竞品声量排行</div>
            <div className="text-xs text-muted-foreground">
              探针回答中被提及/推荐的竞品(自动发现)
            </div>
            {report.competitor_ranking.length === 0 && (
              <div className="text-sm text-muted-foreground">未发现竞品</div>
            )}
            {(() => {
              const byPlat = new Map<string, Map<string, number>>();
              for (const p of report.probes ?? []) {
                for (const c of p.competitors ?? []) {
                  const m = byPlat.get(p.platform) ?? new Map<string, number>();
                  m.set(c, (m.get(c) ?? 0) + 1);
                  byPlat.set(p.platform, m);
                }
              }
              const rows = [...byPlat.entries()]
                .map(([plat, m]) => {
                  const top = [...m.entries()].sort((a, b) => b[1] - a[1])[0];
                  return { plat, name: top[0], count: top[1] };
                })
                .sort((a, b) => b.count - a.count)
                .slice(0, 4);
              if (rows.length === 0) return null;
              return (
                <div
                  className="mb-2 text-xs text-muted-foreground"
                  data-testid="geo-comp-by-engine"
                >
                  {rows.map((r) => (
                    <div key={r.plat}>
                      {engineLabel(r.plat)}最常推:{r.name}({r.count} 次)
                    </div>
                  ))}
                </div>
              );
            })()}
            {report.competitor_ranking.slice(0, 6).map(([name, count]) => (
              <div key={name} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{name}</span>
                <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary/70"
                    style={{
                      width: `${Math.min(100, (count / Math.max(report.competitor_ranking[0]?.[1] ?? 1, 1)) * 100)}%`,
                    }}
                  />
                </div>
                <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">
                  {count}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2 p-6">
            <div className="text-sm font-medium">认知缺口阶段</div>
            <div className="text-xs text-muted-foreground">
              这些购买阶段的问题里,AI 完全没提到你
            </div>
            {(report.brand_portrait?.gap ?? []).length === 0 ? (
              <div className="text-sm text-muted-foreground">各阶段均有曝光</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(report.brand_portrait?.gap ?? []).map((g) => (
                  <span
                    key={g}
                    className="rounded-full border border-rose-500/50 px-2.5 py-0.5 text-xs text-rose-500"
                  >
                    {g === "awareness"
                      ? "认知"
                      : g === "compare"
                        ? "对比"
                        : g === "decide"
                          ? "决策"
                          : g === "doubt"
                            ? "疑虑"
                            : g}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
      <section id="geo-sec-strategy">
        <StrategyTopics report={report} />
      </section>
    </div>
  );
}
